// LanguageSelector — globe button with dropdown of 13 languages.
import React, { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const LanguageSelector = ({ compact = false }) => {
    const { lang, setLang, languages } = useLanguage();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClickAway = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        window.addEventListener("mousedown", onClickAway);
        return () => window.removeEventListener("mousedown", onClickAway);
    }, []);

    const current = languages.find((l) => l.code === lang) || languages[0];

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                aria-label="Change language"
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:border-brand-300 hover:text-brand-700"
                data-testid="language-selector-btn"
            >
                <Globe className="h-3.5 w-3.5" />
                {compact ? (
                    <span className="uppercase">{current.code}</span>
                ) : (
                    <span className="hidden sm:inline">{current.native_name}</span>
                )}
            </button>
            {open && (
                <div
                    className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-xl border border-zinc-100 bg-white shadow-xl"
                    data-testid="language-selector-menu"
                    role="menu"
                >
                    <p className="border-b border-zinc-100 bg-zinc-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                        Language · भाषा
                    </p>
                    <ul className="max-h-80 overflow-y-auto py-1">
                        {languages.map((l) => (
                            <li key={l.code}>
                                <button
                                    type="button"
                                    onClick={() => { setLang(l.code); setOpen(false); }}
                                    className={`flex w-full items-center justify-between px-3 py-2 text-sm transition-colors ${
                                        lang === l.code
                                            ? "bg-brand-50 text-brand-700"
                                            : "text-zinc-700 hover:bg-zinc-50"
                                    }`}
                                    data-testid={`language-option-${l.code}`}
                                >
                                    <span className="flex items-center gap-2">
                                        <span className="text-base">{l.flag_emoji}</span>
                                        <span className="flex flex-col items-start text-left">
                                            <span className="font-medium leading-tight">{l.native_name}</span>
                                            <span className="text-[10px] text-zinc-500 leading-tight">{l.name}</span>
                                        </span>
                                    </span>
                                    {lang === l.code && <Check className="h-3.5 w-3.5 text-brand-700" />}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default LanguageSelector;
